import { useId, useState } from "react";
import { AlertCircle } from "lucide-react";
import type { ContentSource } from "@/services/sanity/types";

interface ContentSourceNoticeProps {
    source: ContentSource;
    message?: string;
}

export default function ContentSourceNotice({ source, message }: ContentSourceNoticeProps) {
    const [isOpen, setIsOpen] = useState(false);
    const tooltipId = useId();

    if (source !== "fallback") {
        return null;
    }

    return (
        <div className="group relative inline-flex">
            <button
                type="button"
                aria-label="Content status notice"
                aria-describedby={tooltipId}
                onClick={() => setIsOpen((currentValue) => !currentValue)}
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-700 shadow-sm transition-colors hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300 dark:border-red-900/70 dark:bg-red-950/50 dark:text-red-200 dark:hover:bg-red-950"
            >
                <AlertCircle size={16} aria-hidden="true" />
                <span className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full border border-white bg-red-500 dark:border-gray-900" />
            </button>

            <div
                id={tooltipId}
                role="tooltip"
                className={`absolute right-0 top-full z-50 mt-2 w-72 rounded-2xl border border-red-200 bg-white px-4 py-3 text-left text-sm leading-relaxed text-gray-700 shadow-xl transition-all dark:border-red-900/70 dark:bg-gray-900 dark:text-gray-200 ${
                    isOpen
                        ? "pointer-events-auto translate-y-0 opacity-100"
                        : "pointer-events-none translate-y-1 opacity-0 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:opacity-100"
                }`}
            >
                {message || "Live content is temporarily unavailable. Showing saved fallback content instead."}
            </div>
        </div>
    );
}
